import { useQuery } from '@tanstack/react-query';

import { useUtilityContext } from '@change-corgi/core/react/utilityContext';
import type { ReadyToSharePlatform } from '@change-corgi/middle-end-gql-schema';

import { getPetitionReadyToSharePostsByPlatform } from 'src/app/shared/api/readyToShare';

const QUERY_KEY = 'ready-to-share';
const CACHE_TIME_MS = Infinity;
const REFETCH_INTERVAL_MS = 5000;
const MAX_REFETCH_COUNT = 5;

export function useFetchReadyToSharePostsByPlatform({
	slug,
	platform,
	shouldExecute,
}: {
	slug: string;
	platform: ReadyToSharePlatform;
	shouldExecute: boolean;
}) {
	const utilityContext = useUtilityContext();

	return useQuery({
		queryKey: [QUERY_KEY, slug, platform],
		queryFn: async () => {
			const result = await getPetitionReadyToSharePostsByPlatform({ slugOrId: slug, platform }, utilityContext);

			if (!result?.petitionBySlugOrId?.readyToSharePosts) {
				throw new Error(`Failed to fetch posts for platform ${platform}`);
			}

			const posts = result.petitionBySlugOrId.readyToSharePosts;

			if (posts.__typename === 'ReadyToSharePostsFailure') {
				throw new Error(posts.message);
			}

			return posts;
		},
		staleTime: CACHE_TIME_MS,
		retry: MAX_REFETCH_COUNT,
		retryDelay: REFETCH_INTERVAL_MS,
		refetchOnWindowFocus: false,
		enabled: shouldExecute,
	});
}
